import { Grid, Typography, TextField, Autocomplete } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import BuildingRegulationsSearchState from "@publicPortal/pages/search/BuildingRegulations/BuildingRegulationsSearchState";
import BuildingRegulationsSimpleSearch from "@publicPortal/pages/search/BuildingRegulations/BuildingRegulationsSimpleSearch";
import { SearchHelper } from "@publicPortal/pages/search/SearchHelper";
import { BuildingRegulationsFilter } from "@shared/publicportal/contracts/BuildingRegulationsFilter";
import { Dayjs } from "dayjs";
import React from "react";
import "../../pages/search/Search.css";
import datePickerValueFormatter from "@mirage/components/helpers/datePickerValueFormatter";

interface BuildingRegulationsAdvSearchProps {
    state: BuildingRegulationsSearchState;
    setter: (modifier: Partial<BuildingRegulationsFilter>) => void;
}

const BuildingRegulationsAdvancedSearch: React.FC<BuildingRegulationsAdvSearchProps> = ({ state, setter }) => {
    return (
        <>
            <BuildingRegulationsSimpleSearch state={state} setter={setter} />
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "70%" } }}>
                <Grid item xs={12} md={12} xl={12}>
                    <Typography variant="h5">Details</Typography>
                </Grid>
                <BcRegulationsDetailFields state={state} setter={setter} />
                <Grid item xs={12} md={12} xl={12}>
                    <TextField
                        value={state.filter.address || ""}
                        onChange={(e) => setter({ address: e.target.value })}
                        key="bcApp-address-key"
                        size="small"
                        id="bcApp-address"
                        label="Address"
                        variant="outlined"
                        sx={{ width: "60%" }}
                    />
                </Grid>
            </Grid>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "45%" } }}>
                <Grid item xs={12} md={12} xl={12}>
                    <Typography variant="h5">Date Fields</Typography>
                </Grid>
                <BcRegulationsDateFields state={state} setter={setter} />
            </Grid>
        </>
    );
};

const BcRegulationsDetailFields: React.FC<BuildingRegulationsAdvSearchProps> = ({ setter, state }) => {
    const fields = [
        <TextField
            value={state.filter.applicant || ""}
            onChange={(e) => setter({ applicant: e.target.value })}
            key="bcApp-applicant-key"
            size="small"
            id="bcApp-applicant"
            label="Applicant"
            variant="outlined"
            className="min-width-18rem"
        />,
        <TextField
            value={state.filter.agent || ""}
            onChange={(e) => setter({ agent: e.target.value })}
            key="bcApp-agent-key"
            size="small"
            id="bcApp-agent"
            label="Agent"
            variant="outlined"
            className="min-width-18rem"
        />,
        <Autocomplete
            options={state.appTypes || []}
            id="bcApp-appTypes"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            key="bcApp-appTypes-key"
            size="small"
            value={state.filter.appType?.length ? state.filter.appType : null}
            inputValue={state.filter.appType}
            onInputChange={(event, value) => event && setter({ appType: value })}
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Application Type")}
        />,
        <Autocomplete
            options={state.partnershipTypes || []}
            id="bcApp-decisions"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            key="bcApp-decisions-key"
            size="small"
            value={state.filter.partnershipType?.length ? state.filter.partnershipType : null}
            inputValue={state.filter.partnershipType}
            onInputChange={(event, value) => event && setter({ partnershipType: value })}
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Partnership Type")}
        />,
        <Autocomplete
            options={state.wards || []}
            inputValue={state.filter.ward}
            value={state.filter.ward?.length ? state.filter.ward : null}
            onInputChange={(event, value) => event && setter({ ward: value })}
            key="bcApp-ward-key"
            size="small"
            id="bcApp-ward"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Ward")}
        />,
        <Autocomplete
            options={state.parishes || []}
            inputValue={state.filter.parish}
            value={state.filter.parish?.length ? state.filter.parish : null}
            onInputChange={(event, value) => event && setter({ parish: value })}
            key="bcApp-parish-key"
            size="small"
            id="bcApp-parish"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Parish")}
        />,
    ];
    return (
        <>
            {fields.map((field) => {
                return (
                    <Grid item xs={12} md={6} xl={4} key={"p-details-" + field.key}>
                        {field}
                    </Grid>
                );
            })}
        </>
    );
};

const BcRegulationsDateFields: React.FC<BuildingRegulationsAdvSearchProps> = ({ setter, state }) => {
    const fields = [
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Decision-From-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.decisionDateFrom)}
                onChange={(value: Dayjs | null) => setter({ decisionDateFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Decision-From") } }}
                label="Decision Date From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Decision-To-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.decisionDateTo)}
                onChange={(value: Dayjs | null) => setter({ decisionDateTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Decision-To") } }}
                label="Decision Date To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Received-Date-From-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.receivedDateFrom)}
                onChange={(value: Dayjs | null) => setter({ receivedDateFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Received-Deadline-From") } }}
                label="Received Date From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Received-Date-To-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.receivedDateTo)}
                onChange={(value: Dayjs | null) => setter({ receivedDateTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Received-Deadline-To") } }}
                label="Received Date To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Validated-Date-From-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.validationDateFrom)}
                onChange={(value: Dayjs | null) => setter({ validationDateFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Validated-Date-From") } }}
                label="Validated Date From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Validated-Date-To-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.validationDateTo)}
                onChange={(value: Dayjs | null) => setter({ validationDateTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Validated-Date-To") } }}
                label="Validated Date To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Started-Date-From-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.dateStartedFrom)}
                onChange={(value: Dayjs | null) => setter({ dateStartedFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Started-Date-From") } }}
                label="Started Date From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Started-Date-To-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.dateStartedTo)}
                onChange={(value: Dayjs | null) => setter({ dateStartedTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Started-Date-To") } }}
                label="Started Date To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Completed-Date-From-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.completedDateFrom)}
                onChange={(value: Dayjs | null) => setter({ completedDateFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Completed-Date-From") } }}
                label="Completed Date From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="bcApp-Completed-Date-To-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.completedDateTo)}
                onChange={(value: Dayjs | null) => setter({ completedDateTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("bcApp-Completed-Date-To") } }}
                label="Completed Date To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
    ];

    return (
        <>
            {fields.map((field) => {
                return (
                    <Grid item xs={12} md={6} xl={6} key={"p-bc-dates-" + field.key}>
                        {field}
                    </Grid>
                );
            })}
        </>
    );
};

export default BuildingRegulationsAdvancedSearch;
